<script setup>
import { HrMenu } from '../primitives';
import HrActionMenu from './HrActionMenu.vue';
import HrIconMenuBtn from './HrIconMenuBtn.vue';

/**
 * Three-dot overflow trigger pre-wired to a dropdown menu — icon button, HrMenu, and HrActionMenu in one.
 *
 * @status recommended
 * @group actions
 * @useWhen A compact secondary-action trigger with a standard dropdown option list
 * @avoidWhen Custom trigger icon or non-standard content — compose HrIconMenuBtn, HrMenu, and HrActionMenu directly
 * @related HrIconMenuBtn
 * @related HrActionMenu
 * @event click Forwarded from the underlying icon button.
 */
defineOptions({ name: 'HrOverflowMenuBtn' });

defineProps({
  /** Material icon name for the trigger button. */
  icon: {
    type: String,
    default: 'more_vert',
  },
  /** Shows a loading spinner in place of the icon. */
  loading: {
    type: Boolean,
    default: false,
  },
  /** Prevents interaction and applies a dimmed appearance. */
  disable: {
    type: Boolean,
    default: false,
  },
  /** Renders a pill-shaped skeleton placeholder instead of the button. */
  pending: {
    type: Boolean,
    default: false,
  },
  /**
   * Option list forwarded to HrActionMenu — flat or grouped; see HrActionMenu for the entry shape.
   * @type {import('./HrActionMenu.vue').HrActionMenuEntry[]}
   * @example [{"label":"Copy","icon":"content_copy"},{"label":"Rename"},{"label":"Delete","destructive":true}]
   */
  options: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits({
  /** Fired when the user selects an option; payload is the selected HrActionMenuOption object. */
  select: null,
});
</script>

<template>
  <hr-icon-menu-btn
    :icon="icon"
    :loading="loading"
    :disable="disable"
    :pending="pending"
  >
    <hr-menu>
      <hr-action-menu
        :options="options"
        @select="emit('select', $event)"
      />
    </hr-menu>
  </hr-icon-menu-btn>
</template>
